import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import classnames from "classnames";
import { useTheme } from "@mui/material/styles";
import Slide from "@mui/material/Slide";
import { Tooltip } from "../../Tooltip/index.js";
import { useFilesService, useImageConverter, useImageRotator } from "../../services/hooks/index.js";
import { useIsDesktop } from "../../hooks/index.js";
import { Icon } from "../../Icon/index.js";
import { Link } from "../../Link/index.js";
import { Button } from "../../Button/index.js";
import { FilePreview } from "../../FilePreview/index.js";
import { Box } from "../../Box/index.js";
import { Grid } from "../../Grid/index.js";
import { Paper } from "../../Paper/index.js";
import { CircularProgress } from "../../CircularProgress/index.js";
import { Typography } from "../../Typography/index.js";
import { FileError, HelperText, Label } from "../common/index.js";
import { FillBanner } from "./FillBanner/index.js";
import FileInput_styles from "./FileInput.styles.js";
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) resolve(value);
    else Promise.resolve(value).then(_next, _throw);
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(void 0);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function FileInput_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else FileInput_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
function _object_without_properties(source, excluded) {
    if (null == source) return {};
    var target = {}, sourceKeys, key, i;
    if ("u" > typeof Reflect && Reflect.ownKeys) {
        sourceKeys = Reflect.ownKeys(Object(source));
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (!(excluded.indexOf(key) >= 0)) {
                if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
            }
        }
        return target;
    }
    target = _object_without_properties_loose(source, excluded);
    if (Object.getOwnPropertySymbols) {
        sourceKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (!(excluded.indexOf(key) >= 0)) {
                if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
            }
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (null == source) return {};
    var target = {}, sourceKeys = Object.getOwnPropertyNames(source), key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (!(excluded.indexOf(key) >= 0)) {
            if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
        }
    }
    return target;
}
const ALLOWED_TYPES = [
    'image/png',
    'image/jpeg',
    'image/apng',
    'image/gif',
    'image/webp',
    'image/avif'
];
const FileInput = (_0)=>{
    let { id: htmlId, value, onChange, onBlur = ()=>{}, label, labelIcon, tooltip, variant = 'input', placeholder = 'Перенесите файл, что бы прикрепить документ', required = false, disabled = false, loading = false, loadingText, cache, fullWidth, error = false, errorText = '', helperText = '', onCacheSuccess = ()=>{}, onCacheError = ()=>{}, bannerText, autorecognitionQr = false, testId = 'test-id-file-input', withAuth, withZoom = false, validators, allowedTypes = ALLOWED_TYPES } = _0, rest = _object_without_properties(_0, [
        "id",
        "value",
        "onChange",
        "onBlur",
        "label",
        "labelIcon",
        "tooltip",
        "variant",
        "placeholder",
        "required",
        "disabled",
        "loading",
        "loadingText",
        "cache",
        "fullWidth",
        "error",
        "errorText",
        "helperText",
        "onCacheSuccess",
        "onCacheError",
        "bannerText",
        "autorecognitionQr",
        "testId",
        "withAuth",
        "withZoom",
        "validators",
        "allowedTypes"
    ]);
    var _ref;
    var _previewBlock_current, _blobDocument_current, _blobDocument_current1;
    const { classes } = FileInput_styles();
    const reactId = useId();
    const inputId = useMemo(()=>null != htmlId ? htmlId : reactId, [
        htmlId,
        reactId
    ]);
    const isDesktop = useIsDesktop();
    const filesService = useFilesService();
    const imageRotator = useImageRotator();
    const imageConverter = useImageConverter();
    const previewBlock = useRef(null);
    const blobDocument = useRef(null);
    const theme = useTheme();
    const [isFileLoading, setIsFileLoading] = useState(loading);
    const [isButtonsVisible, setIsButtonsVisible] = useState(false);
    const [cacheFileName, setCacheFilename] = useState('');
    const [validationErrorMessage, setValidationErrorMessage] = useState('');
    const [invalidFileName, setInvalidFileName] = useState('');
    const finalError = error || validationErrorMessage;
    const isLoading = loading || isFileLoading;
    const isDisabled = disabled || isLoading;
    const previewWidth = 296;
    let previewHeight;
    previewHeight = isDesktop || autorecognitionQr || value && !error && !isLoading ? 418 : bannerText && !value ? 144 : 92;
    const previewBlockRect = null == previewBlock ? void 0 : null == (_previewBlock_current = previewBlock.current) ? void 0 : _previewBlock_current.getBoundingClientRect();
    const previewBlockWidth = null != (_ref = null == previewBlockRect ? void 0 : previewBlockRect.width) ? _ref : previewWidth;
    const previewResponsiveHeight = previewHeight * previewBlockWidth / previewWidth;
    const handleChange = useCallback((nextValue)=>{
        if (cacheFileName) setCacheFilename('');
        onChange(nextValue);
    }, [
        onChange,
        cacheFileName
    ]);
    useEffect(()=>{
        blobDocument.current = null;
    }, [
        value
    ]);
    const load = useCallback(()=>_async_to_generator(function*() {
            if (blobDocument.current) return blobDocument.current;
            if ('string' == typeof (null == value ? void 0 : value.source)) {
                const fileData = yield filesService.getFile(value.source, withAuth);
                const { file, filename } = yield fileData;
                if (filename) setCacheFilename(filename);
                blobDocument.current = yield file;
                return file;
            }
            if (null == value ? void 0 : value.source) blobDocument.current = value.source;
            return (null == value ? void 0 : value.source) || new Blob();
        })(), [
        filesService,
        null == value ? void 0 : value.source,
        withAuth
    ]);
    const isHEICFile = (file_0)=>{
        const fileName = file_0.name.toLowerCase();
        const isHEICByExtension = fileName.endsWith('.heic') || fileName.endsWith('.heif');
        const isHEICByType = [
            'image/heic',
            'image/heif',
            'image/heif-sequence',
            'image/heic-sequence'
        ].includes(file_0.type);
        const isHEICOnWindows = ('' === file_0.type || 'application/octet-stream' === file_0.type) && isHEICByExtension;
        return isHEICByExtension || isHEICByType || isHEICOnWindows;
    };
    const isConvertableImageFile = (file_1)=>{
        const fileName_0 = file_1.name.toLowerCase();
        const isConvertableByExtension = fileName_0.endsWith('.dng') || fileName_0.endsWith('.tiff');
        const isConvertableByType = [
            'image/tiff',
            'image/dng',
            'image/x-adobe-dng'
        ].includes(file_1.type);
        return isConvertableByType || isConvertableByExtension;
    };
    const clearValidationState = useCallback(()=>{
        setValidationErrorMessage('');
        setInvalidFileName('');
    }, []);
    const validateUploadedFile = useCallback((uploadedFile)=>_async_to_generator(function*() {
            if (!validators) return false;
            try {
                const validationResult = yield validators({
                    source: uploadedFile
                });
                if (validationResult) {
                    setValidationErrorMessage(validationResult);
                    setInvalidFileName(uploadedFile.name);
                    return true;
                }
                clearValidationState();
                return false;
            } catch (validationError) {
                setValidationErrorMessage('Ошибка при проверке файла');
                setInvalidFileName(uploadedFile.name);
                return true;
            }
        })(), [
        validators,
        clearValidationState
    ]);
    const handleCachedUpload = useCallback((uploadedFile_0, isWrongFormat)=>_async_to_generator(function*() {
            if (!cache) return;
            if (isWrongFormat) {
                const [fileCacheResponse, blob] = yield Promise.all([
                    cache(uploadedFile_0).catch(()=>null),
                    imageConverter.convert(uploadedFile_0).catch(()=>null)
                ]);
                const cacheId = null == fileCacheResponse ? void 0 : fileCacheResponse.file_id;
                const cacheExpiredAt = null == fileCacheResponse ? void 0 : fileCacheResponse.expired_at;
                const convertedFileName = uploadedFile_0.name.replace(/\.[^.]+$/, '.jpeg');
                const convertedFile = blob && new File([
                    blob
                ], convertedFileName, {
                    type: blob.type
                });
                blobDocument.current = convertedFile || new Blob();
                if (convertedFile && validators) {
                    const validationResult_0 = yield validators({
                        source: convertedFile
                    });
                    if (validationResult_0) {
                        setValidationErrorMessage(validationResult_0);
                        setInvalidFileName(uploadedFile_0.name);
                        return;
                    }
                }
                handleChange({
                    value: cacheId ? JSON.stringify({
                        type: 'file_cache',
                        id: cacheId,
                        expiredAt: cacheExpiredAt || ''
                    }) : convertedFile || uploadedFile_0,
                    source: convertedFile || uploadedFile_0
                });
            } else try {
                const { file_id: cacheId_0, expired_at: cacheExpiredAt_0 } = yield cache(uploadedFile_0);
                blobDocument.current = uploadedFile_0;
                handleChange({
                    value: JSON.stringify({
                        type: 'file_cache',
                        id: cacheId_0,
                        expiredAt: cacheExpiredAt_0
                    }),
                    source: uploadedFile_0
                });
                onCacheSuccess(cacheId_0.toString());
            } catch (e) {
                onCacheError();
            }
        })(), [
        cache,
        handleChange,
        imageConverter,
        onCacheError,
        onCacheSuccess,
        validators
    ]);
    const handleNonCachedUpload = useCallback((uploadedFile_1, isWrongFormat_0)=>_async_to_generator(function*() {
            if (isWrongFormat_0) {
                const blob_0 = yield imageConverter.convert(uploadedFile_1);
                const convertedFileName_0 = uploadedFile_1.name.replace(/\.[^.]+$/, '.jpeg');
                const convertedFile_0 = new File([
                    blob_0
                ], convertedFileName_0, {
                    type: blob_0.type
                });
                if (validators) {
                    const validationResult_1 = yield validators({
                        source: convertedFile_0
                    });
                    if (validationResult_1) {
                        setValidationErrorMessage(validationResult_1);
                        setInvalidFileName(uploadedFile_1.name);
                        return;
                    }
                }
                blobDocument.current = blob_0;
                handleChange({
                    value: convertedFile_0,
                    source: convertedFile_0
                });
            } else {
                blobDocument.current = uploadedFile_1;
                handleChange({
                    value: uploadedFile_1,
                    source: uploadedFile_1
                });
            }
        })(), [
        handleChange,
        imageConverter,
        validators
    ]);
    const onDrop = useCallback((files)=>_async_to_generator(function*() {
            try {
                var _document_activeElement, _document;
                if (files && files.length) {
                    setIsFileLoading(true);
                    clearValidationState();
                    const uploadedFile_3 = files[0];
                    const isWrongFormat_1 = isHEICFile(uploadedFile_3) || isConvertableImageFile(uploadedFile_3);
                    if (!isWrongFormat_1) {
                        const validationFailed = yield validateUploadedFile(uploadedFile_3);
                        if (validationFailed) return void setIsFileLoading(false);
                    }
                    if (cache) yield handleCachedUpload(uploadedFile_3, isWrongFormat_1);
                    else yield handleNonCachedUpload(uploadedFile_3, isWrongFormat_1);
                    setIsFileLoading(false);
                } else handleChange(void 0);
                null == (_document = document) || null == (_document_activeElement = _document.activeElement) || _document_activeElement.blur();
                onBlur();
            } catch (unused) {
                const uploadedFile_2 = files[0];
                setValidationErrorMessage('Ошибка при загрузке файла');
                setInvalidFileName(uploadedFile_2.name);
                setIsFileLoading(false);
            }
        })(), [
        cache,
        clearValidationState,
        handleCachedUpload,
        handleChange,
        handleNonCachedUpload,
        onBlur,
        validateUploadedFile
    ]);
    const { open, getRootProps, getInputProps } = useDropzone({
        disabled: isDisabled,
        onDrop
    });
    const rotateDocument = ()=>{
        load().then((blob_1)=>{
            imageRotator.rotate(blob_1).then((updatedBlob)=>{
                if (updatedBlob) {
                    const format = updatedBlob.type.split('/').pop();
                    const file_2 = new File([
                        updatedBlob
                    ], `image.${format}`, {
                        type: updatedBlob.type
                    });
                    onDrop([
                        file_2
                    ]);
                }
            });
        });
    };
    const removeDocument = ()=>{
        handleChange(void 0);
        setIsButtonsVisible(false);
    };
    const createInputText = useCallback(()=>{
        if (isLoading) return 'Загрузка...';
        if (value) return 'string' != typeof value.source && value.source.name || 'string' == typeof value.value && (cacheFileName || label);
        if (invalidFileName) return invalidFileName;
        return placeholder;
    }, [
        cacheFileName,
        isLoading,
        label,
        placeholder,
        value,
        invalidFileName
    ]);
    const errorDisplay = useMemo(()=>{
        if (validationErrorMessage) return /*#__PURE__*/ jsx(FileError, {
            text: validationErrorMessage
        });
        if (error) return /*#__PURE__*/ jsx(FileError, {
            text: errorText
        });
        if (helperText && !value) return /*#__PURE__*/ jsx(HelperText, {
            text: helperText
        });
        return null;
    }, [
        error,
        errorText,
        validationErrorMessage,
        helperText,
        value
    ]);
    const inputLabel = label ? /*#__PURE__*/ jsx(Label, {
        label: label,
        required: required,
        tooltip: tooltip,
        icon: labelIcon,
        inputId: inputId
    }) : null;
    const getInputTextColor = ()=>{
        if (isDisabled) return 'text.light.secondary';
        if (validationErrorMessage || value) return 'text.light.primary';
        return 'text.light.tertirary';
    };
    const inputVariantDropzone = /*#__PURE__*/ jsxs("div", _object_spread_props(_object_spread({}, getRootProps({
        className: 'dropzone aqa_file_input_dropzone'
    })), {
        "data-testid": testId,
        children: [
            /*#__PURE__*/ jsx("input", _object_spread_props(_object_spread({
                id: inputId
            }, rest, getInputProps()), {
                "data-testid": `${testId}-input`
            })),
            /*#__PURE__*/ jsx("div", {
                className: classnames(classes.dropzone, (error || validationErrorMessage) && classes.error, isDisabled && classes.disabled),
                children: /*#__PURE__*/ jsx(Typography, {
                    variant: "body2",
                    color: getInputTextColor(),
                    className: classes.inputText,
                    children: createInputText()
                })
            })
        ]
    }));
    const inputVariantButton = /*#__PURE__*/ jsx(Button, {
        variant: "secondary",
        size: "large",
        onClick: open,
        disabled: disabled,
        testId: `${testId}-button`,
        icon: isLoading ? /*#__PURE__*/ jsx(CircularProgress, {
            size: 20
        }) : /*#__PURE__*/ jsx(Icon, {
            name: "attach",
            size: "24"
        })
    });
    return 'input' === variant ? /*#__PURE__*/ jsxs(Box, {
        className: classnames(classes.fileInput, 'aqa_file_input'),
        children: [
            fullWidth ? /*#__PURE__*/ jsxs(Box, {
                children: [
                    inputLabel,
                    /*#__PURE__*/ jsxs(Box, {
                        display: "flex",
                        gap: "20",
                        children: [
                            /*#__PURE__*/ jsx(Box, {
                                flexGrow: 1,
                                maxWidth: "calc(100% - 76px)",
                                children: inputVariantDropzone
                            }),
                            /*#__PURE__*/ jsx(Box, {
                                children: inputVariantButton
                            })
                        ]
                    })
                ]
            }) : /*#__PURE__*/ jsxs(Grid, {
                container: true,
                spacing: "20",
                children: [
                    /*#__PURE__*/ jsxs(Grid, {
                        item: true,
                        xs: 9,
                        md: 8,
                        children: [
                            inputLabel,
                            inputVariantDropzone
                        ]
                    }),
                    /*#__PURE__*/ jsx(Grid, {
                        item: true,
                        xs: 3,
                        md: 4,
                        children: /*#__PURE__*/ jsx(Box, {
                            display: "flex",
                            height: "100%",
                            alignItems: "end",
                            children: inputVariantButton
                        })
                    })
                ]
            }),
            /*#__PURE__*/ jsx(Grid, {
                container: true,
                children: /*#__PURE__*/ jsx(Grid, {
                    item: true,
                    xs: 9,
                    md: 8,
                    children: errorDisplay
                })
            }),
            value && !finalError && /*#__PURE__*/ jsx(Box, {
                mt: {
                    xs: '8',
                    md: '16'
                },
                width: {
                    xs: 96,
                    md: 144
                },
                height: {
                    xs: 96,
                    md: 144
                },
                children: /*#__PURE__*/ jsx(Tooltip, {
                    title: createInputText(),
                    placement: "bottom",
                    children: /*#__PURE__*/ jsxs(Box, {
                        position: "relative",
                        children: [
                            !disabled && /*#__PURE__*/ jsx(Box, {
                                position: "absolute",
                                top: 7,
                                right: 8,
                                zIndex: 1,
                                children: /*#__PURE__*/ jsx(Button, {
                                    size: "extraSmall",
                                    variant: "quaternaryAccent",
                                    onClick: removeDocument,
                                    testId: `${testId}-clear`,
                                    icon: /*#__PURE__*/ jsx(Icon, {
                                        name: "cancel",
                                        size: "16",
                                        color: "original.brand.primary"
                                    })
                                })
                            }),
                            /*#__PURE__*/ jsx(Paper, {
                                children: /*#__PURE__*/ jsx(FilePreview, {
                                    shape: "square",
                                    onLoad: load,
                                    clickable: true,
                                    withZoom: withZoom
                                })
                            })
                        ]
                    })
                })
            })
        ]
    }) : /*#__PURE__*/ jsxs(Box, {
        width: fullWidth ? '100%' : previewWidth,
        testId: testId,
        children: [
            label && /*#__PURE__*/ jsx(Label, {
                label: label,
                required: required,
                tooltip: tooltip,
                inputId: inputId
            }),
            /*#__PURE__*/ jsxs(Box, {
                position: "relative",
                children: [
                    isLoading ? /*#__PURE__*/ jsx(Box, {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: 418,
                        bgcolor: "bg.light.primary",
                        border: `${theme.tokens.border.m}px dashed`,
                        borderColor: "stroke.primary",
                        radius: "l",
                        children: /*#__PURE__*/ jsxs(Box, {
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: fullWidth && !autorecognitionQr ? previewResponsiveHeight : previewHeight,
                            children: [
                                bannerText && !value && /*#__PURE__*/ jsx(FillBanner, {
                                    children: bannerText
                                }),
                                /*#__PURE__*/ jsxs(Box, {
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    width: {
                                        xs: 250
                                    },
                                    mt: isDesktop || !bannerText || value ? '0' : '8',
                                    gap: "8",
                                    children: [
                                        /*#__PURE__*/ jsx(CircularProgress, {
                                            size: 20
                                        }),
                                        loadingText || /*#__PURE__*/ jsx(Box, {
                                            mb: {
                                                xs: '0',
                                                md: '12'
                                            },
                                            color: isDesktop || autorecognitionQr ? 'text.light.tertirary' : 'original.brand.primary',
                                            children: /*#__PURE__*/ jsx(Typography, {
                                                display: "block",
                                                variant: isDesktop || autorecognitionQr ? 'body2' : 'caption',
                                                color: "inherit",
                                                align: "center",
                                                children: "Пожалуйста, подождите"
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    }) : /*#__PURE__*/ jsxs("div", _object_spread_props(_object_spread({}, getRootProps({
                        className: 'dropzone'
                    })), {
                        children: [
                            /*#__PURE__*/ jsx("input", _object_spread_props(_object_spread({
                                id: inputId
                            }, rest, getInputProps()), {
                                "data-testid": `${testId}-input`
                            })),
                            /*#__PURE__*/ jsx("div", {
                                ref: previewBlock,
                                className: classnames(classes.previewDropzone, (error || validationErrorMessage) && classes.error, isDisabled && classes.disabled),
                                children: /*#__PURE__*/ jsxs(Box, {
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: fullWidth && !autorecognitionQr ? previewResponsiveHeight : previewHeight,
                                    children: [
                                        bannerText && !value && /*#__PURE__*/ jsx(FillBanner, {
                                            children: bannerText
                                        }),
                                        /*#__PURE__*/ jsxs(Box, {
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            width: {
                                                xs: 154,
                                                md: 200
                                            },
                                            mt: isDesktop || !bannerText || value ? '0' : '56',
                                            children: [
                                                /*#__PURE__*/ jsx(Box, {
                                                    mb: "4",
                                                    height: isDesktop || autorecognitionQr ? 56 : 32,
                                                    width: isDesktop || autorecognitionQr ? 56 : 32,
                                                    fontSize: {
                                                        xs: autorecognitionQr ? 56 : 32,
                                                        md: 56
                                                    },
                                                    children: /*#__PURE__*/ jsx(Icon, {
                                                        name: "vkhrtek_file",
                                                        size: "48"
                                                    })
                                                }),
                                                /*#__PURE__*/ jsx(Box, {
                                                    mb: {
                                                        xs: autorecognitionQr ? '12' : '0',
                                                        md: '12'
                                                    },
                                                    color: isDesktop || autorecognitionQr ? 'text.light.tertirary' : 'original.brand.primary',
                                                    children: /*#__PURE__*/ jsx(Typography, {
                                                        display: "block",
                                                        variant: isDesktop || autorecognitionQr ? 'body2' : 'caption',
                                                        color: "inherit",
                                                        align: "center",
                                                        children: placeholder || 'Загрузите изображение или PDF файл'
                                                    })
                                                }),
                                                (isDesktop || autorecognitionQr) && /*#__PURE__*/ jsx(Button, {
                                                    variant: "secondary",
                                                    size: "small",
                                                    disabled: disabled,
                                                    testId: `${testId}-upload`,
                                                    children: "Загрузить"
                                                })
                                            ]
                                        })
                                    ]
                                })
                            })
                        ]
                    })),
                    value && !finalError && !isLoading && /*#__PURE__*/ jsxs("div", {
                        className: classes.filePreview,
                        "data-testid": `${testId}-preview`,
                        onMouseEnter: ()=>setIsButtonsVisible(true),
                        onMouseLeave: ()=>setIsButtonsVisible(false),
                        children: [
                            /*#__PURE__*/ jsxs(Box, {
                                position: "relative",
                                width: "100%",
                                height: "100%",
                                children: [
                                    /*#__PURE__*/ jsx(Box, {
                                        height: "100%",
                                        children: /*#__PURE__*/ jsx(FilePreview, {
                                            withFlex: true,
                                            onLoad: load,
                                            withZoom: true
                                        })
                                    }),
                                    !isDesktop && /*#__PURE__*/ jsx(Box, {
                                        position: "absolute",
                                        top: 7,
                                        right: 8,
                                        zIndex: 1,
                                        children: /*#__PURE__*/ jsx(Button, {
                                            size: "extraSmall",
                                            variant: "quaternaryAccent",
                                            onClick: removeDocument,
                                            testId: `${testId}-clear`,
                                            icon: /*#__PURE__*/ jsx(Icon, {
                                                name: "cancel",
                                                size: "16",
                                                color: "original.brand.primary"
                                            })
                                        })
                                    })
                                ]
                            }),
                            isDesktop && /*#__PURE__*/ jsx(Slide, {
                                direction: "up",
                                in: isButtonsVisible,
                                mountOnEnter: true,
                                unmountOnExit: true,
                                timeout: 500,
                                children: /*#__PURE__*/ jsxs("div", {
                                    className: classes.previewButtons,
                                    children: [
                                        /*#__PURE__*/ jsx(Box, {
                                            children: (null == blobDocument ? void 0 : null == (_blobDocument_current = blobDocument.current) ? void 0 : _blobDocument_current.type.startsWith('image/')) && /*#__PURE__*/ jsx(Tooltip, {
                                                title: "Повернуть изображение против часовой стрелки",
                                                placement: "bottom",
                                                children: /*#__PURE__*/ jsx("div", {
                                                    children: /*#__PURE__*/ jsx(Link, {
                                                        size: "medium",
                                                        onClick: rotateDocument,
                                                        testId: `${testId}-rotate`,
                                                        startIcon: /*#__PURE__*/ jsx(Icon, {
                                                            name: "rotate_left",
                                                            size: "16",
                                                            color: "original.brand.primary"
                                                        }),
                                                        stroke: false
                                                    })
                                                })
                                            })
                                        }),
                                        !disabled && /*#__PURE__*/ jsx(Link, {
                                            size: "small",
                                            onClick: removeDocument,
                                            testId: `${testId}-clear`,
                                            endIcon: /*#__PURE__*/ jsx(Icon, {
                                                name: "delete_outline",
                                                size: "16",
                                                color: "original.brand.primary"
                                            }),
                                            stroke: false,
                                            children: "Удалить"
                                        })
                                    ]
                                })
                            }),
                            !isDesktop && allowedTypes.includes((null == blobDocument ? void 0 : null == (_blobDocument_current1 = blobDocument.current) ? void 0 : _blobDocument_current1.type) || '') && /*#__PURE__*/ jsx(Box, {
                                position: "absolute",
                                right: 16,
                                bottom: 16,
                                children: /*#__PURE__*/ jsx(Button, {
                                    variant: "quaternary",
                                    testId: `${testId}-rotate`,
                                    icon: /*#__PURE__*/ jsx(Icon, {
                                        name: "rotate_left_outline",
                                        size: "16",
                                        color: "original.brand.primary"
                                    }),
                                    onClick: rotateDocument
                                })
                            })
                        ]
                    })
                ]
            }),
            finalError && errorDisplay
        ]
    });
};
export { FileInput };
